const PaymentChannelIDEnum = require('@/lib/Channels/PaymentChannelIDEnum');
const version = require('@temp/js/version');

/**
 * @constructor
 */
const AppConfig = function () {
    /**
     * By default use the production dsn.
     *
     * @type {string}
     */
    this.sentryDsn = '';

    /**
     * @type {boolean}
     */
    this.sentryEnabled = true;

    /**
     * Domain with the (beta.)epos. and the port removed.
     *
     * @type {string}
     */
    this.domain = '';

    /**
     * @type {boolean}
     */
    this.isBeta = false;

    /**
     * @type {string}
     */
    this.environment = 'Production';

    /**
     * @type {boolean}
     */
    this.isDev = false;

    /**
     * URL to the HTTP proxy server provided by ProPoint Agent V3.
     *
     * @type {string}
     */
    this.agentHttpProxyUrl = 'https://localhost.dtapps.co.uk:44302';

    /**
     * Name of the header to send to the HTTP proxy server to let it know where to send requests.
     *
     * @type {string}
     */
    this.agentHttpProxyBaseUrlHeader = 'X-Proxy-Base-Url';

    /**
     * Path to 'app.' for the current domain.
     * e.g. https://app.digitickets.co.uk
     *
     * @type {string}
     */
    this.appUrl = '';

    /**
     * Path to 'backoffice.' for the current domain.
     * e.g. https://backoffice.digitickets.co.uk
     *
     * @type {string}
     */
    this.backofficeUrl = '';

    /**
     * Path to 'api.' for the current domain, with version path added.
     * e.g. https://api.digitickets.co.uk/v2/
     *
     * @type {string}
     */
    this.apiBaseUrl = '';

    /**
     * Path to the EPOS application (without any specific screen at the end.)
     * e.g. https://epos.digitickets.co.uk
     *
     * @type {string}
     */
    this.eposUrl = '';

    /**
     * Path to socket server for the current environment.
     * e.g. https://socket.digitickets.test:2053
     *
     * Note this is the socket server for pushing messages to the kitchen screen, nothing to do with ProPoint Agent.
     *
     * @type {string}
     */
    this.socketUrl = '';

    /**
     * Sent as salesChannelID on every API request, as paymentChannelID on payments, and as channelID when
     * loading items.
     *
     * @type {number}
     */
    this.channelID = PaymentChannelIDEnum.EPOS;

    /**
     * @type {number}
     */
    this.maxSessionsToSyncPerRequest = 2000;

    /**
     * @type {string}
     */
    this.supportEmail = 'support@digitickets.co.uk';

    /**
     * @type {string}
     */
    this.supportTelephone = '+44 333 577 6002';

    /**
     * @type {string}
     */
    this.supportTelephoneUrl = '+443335776002';

    /**
     * @type {string}
     */
    this.supportUrl = '';

    /**
     * @type {Date}
     */
    this.eposVersion = new Date(version.builtAt);

    /**
     * @type {string}
     */
    this.eposBranch = version.branch;

    /**
     * @type {string}
     */
    this.eposCommit = version.commit;

    /**
     * Show the "Login With Microsoft Entra ID" button on the login page?
     * This can be true, false, or null.
     * If true: The button will always be displayed.
     * If false: The button will never be displayed.
     * If null: The user can show/hide the button from the settings button on the login page.
     *
     * @type {boolean|null}
     */
    this.entraIDLoginEnabled = null;

    /**
     * How often do we check the version.json to see if a new version of ProPoint is available?
     *
     * @type {number}
     */
    this.updateCheckFrequency = 5 * 60 * 1000; // 5 minutes

    /**
     * @type {string}
     */
    this.eInvoicingUrl = '';

    /**
     * @type {string}
     */
    this.waiversUrl = '';

    /**
     * How often do we check to see if we are online / offline in seconds.
     *
     * @type {number}
     */
    this.statusCheckFrequency = 30; // 30 seconds

    this.load();

    // Parse the current location info upon creation.
    if (typeof window !== 'undefined' && window.location) {
        this.setLocation(window.location);
    }
};

AppConfig.prototype = {
    load() {
        // Set all the appropriate values from the `window.config` definition.
        if (typeof window !== 'undefined' && window.config) {
            Object.keys(window.config).forEach((key) => {
                if (this.hasOwnProperty(key)) {
                    this[key] = window.config[key];
                }
            });
        }
    },

    /**
     * Return the json object containing ProPoint version details.
     *
     * @returns {{builtAt: string, commit: string, branch: string}}
     */
    getVersion() {
        return version;
    },

    /**
     * @param {Location} location
     */
    setLocation(location) {
        this.parseHost(location.host);
    },

    /**
     * @private
     *
     * @param {string} host
     */
    parseHost(host) {
        // Remove the port from the end.
        host = host.split(':')[0];

        if (host.indexOf('beta.epos.') === 0) {
            this.domain = host.substr('beta.epos.'.length);
            this.isBeta = true;
        } else if (host.indexOf('epos') === 0) {
            this.domain = host.substr('epos.'.length);
        } else {
            this.domain = host;
        }
    },

    /**
     * @param {string} screen
     * @return {string}
     */
    getEposUrl(screen) {
        const portString = ![80, 443].includes(window.location.port) ? ':' + window.location.port : '';

        return this.eposUrl + portString + '/' + screen;
    },

    /**
     * e.g. epos/kitchen/kitchen-customer
     *
     * @return {string}
     */
    getCurrentScreen() {
        return window.location.pathname.split('/')[1] || null;
    },

    /**
     * e.g. https://epos.digitickets.co.uk/epos
     *
     * @return {string}
     */
    getCurrentScreenUrl() {
        return this.getEposUrl(this.getCurrentScreen());
    },
};

module.exports = AppConfig;
